import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RefreshCw, TrendingUp, Users, Search, FolderPlus, Clock, Activity } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import CoreValueMetricCard from "./CoreValueMetricCard";
import CoreValueChart from "./CoreValueChart";
import EngagementDefinitions from "./EngagementDefinitions";
import AnalyticsDebugPanel from "./AnalyticsDebugPanel";

interface ContentSavingMetrics {
  total_content_saved: number;
  manual_saves: number;
  extension_saves: number;
  import_saves: number;
  daily_saves: Array<{ date: string; count: number }>;
}

interface OrganizationMetrics {
  collections_created: number;
  tags_created: number;
  boards_created: number;
  content_organized: number;
  content_edited: number;
  daily_organization: Array<{ date: string; count: number }>;
}

interface DiscoveryMetrics {
  total_navigation: number;
  collection_navigation: number;
  tag_navigation: number;
  board_navigation: number;
  searches_performed: number;
  search_clicks: number;
  daily_discovery: Array<{ date: string; count: number }>;
}

interface EngagementMetrics {
  total_active_users: number;
  saver_count: number;
  organizer_count: number;
  discoverer_count: number;
  curator_count: number;
  level_distribution: {
    Saver: number;
    Organizer: number;
    Discoverer: number;
    Curator: number;
  };
}

interface RecentEvent {
  id: string;
  event_type: string;
  event_category: string;
  timestamp: string;
  user_id?: string;
}

const CoreValueDashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState(7); // Last 7 days as default
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [contentSaving, setContentSaving] = useState<ContentSavingMetrics | null>(null);
  const [organization, setOrganization] = useState<OrganizationMetrics | null>(null);
  const [discovery, setDiscovery] = useState<DiscoveryMetrics | null>(null);
  const [engagement, setEngagement] = useState<EngagementMetrics | null>(null);
  const [recentEvents, setRecentEvents] = useState<RecentEvent[]>([]);
  const [dataQualityScore, setDataQualityScore] = useState<number>(0);

  // FIXED: Generate exact consecutive dates ending with today
  const generateExpectedDates = (dateRange: number): string[] => {
    const dates: string[] = [];
    const today = new Date();
    
    if (dateRange === 1) {
      // Today only
      dates.push(today.toISOString().split('T')[0]);
    } else {
      // Generate exactly N consecutive days ending with today
      for (let i = 0; i < dateRange; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() - (dateRange - 1) + i);
        dates.push(date.toISOString().split('T')[0]);
      }
    }
    
    console.log(`[CoreValue Analytics] Generated expected dates for ${dateRange} days:`, dates);
    return dates;
  };

  // FIXED: Helper function to enforce exact date range with proper deduplication
  const parseDailyData = (data: any, expectedDays: number): Array<{ date: string; count: number }> => {
    if (!data) {
      console.warn('[CoreValue Analytics] No daily data provided');
      return [];
    }
    
    // Generate the exact dates we expect
    const expectedDates = generateExpectedDates(expectedDays);
    console.log(`[CoreValue Analytics] Expected dates for ${expectedDays} days:`, expectedDates);
    
    if (Array.isArray(data)) {
      // Create a map to deduplicate and aggregate by date
      const dateMap = new Map<string, number>();
      
      data.forEach(item => {
        if (item && typeof item === 'object' && 
            typeof item.date === 'string' && 
            typeof item.count === 'number' && 
            !isNaN(item.count)) {
          
          // Normalize date to YYYY-MM-DD format
          const dateKey = new Date(item.date).toISOString().split('T')[0];
          
          // Only include dates that are in our expected range
          if (expectedDates.includes(dateKey)) {
            // Only use the count once per unique date, don't accumulate
            if (!dateMap.has(dateKey)) {
              dateMap.set(dateKey, item.count);
            }
          }
        }
      });
      
      // Create result array with exactly the expected dates, filling missing dates with 0
      const result = expectedDates.map(dateKey => ({
        date: dateKey,
        count: dateMap.get(dateKey) || 0
      }));
      
      console.log(`[CoreValue Analytics] Processed ${data.length} raw items into ${result.length} daily points (expected ${expectedDays} days):`, result);
      
      if (result.length !== expectedDays) {
        console.error(`[CoreValue Analytics] MISMATCH: Expected ${expectedDays} days but generated ${result.length} days of data`);
      }
      
      return result;
    }
    
    console.warn('[CoreValue Analytics] Daily data is not an array:', typeof data);
    return [];
  };

  // Calculate data quality score based on available metrics (excluding system noise)
  const calculateDataQuality = (saving: any, org: any, disc: any, eng: any): number => {
    let score = 0;
    const maxScore = 10;
    
    // Check if we have meaningful data for each category
    if (saving?.total_content_saved > 0) score += 3;
    if (org?.collections_created > 0 || org?.tags_created > 0 || org?.boards_created > 0) score += 2;
    if (disc?.total_navigation > 0 || disc?.searches_performed > 0) score += 2;
    if (eng?.total_active_users > 0) score += 2;
    
    // Check if we have daily data
    if (saving?.daily_saves?.length > 0) score += 0.5;
    if (org?.daily_organization?.length > 0) score += 0.5;
    
    return Math.round((score / maxScore) * 100);
  };

  // Enhanced event fetching for debugging with category filtering
  const fetchRecentEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('user_activity_events')
        .select('id, event_type, event_category, timestamp, user_id, event_data')
        .order('timestamp', { ascending: false })
        .limit(20);

      if (error) {
        console.error('[CoreValue Analytics] Error fetching recent events:', error);
        return;
      }

      setRecentEvents(data || []);
      
      // Enhanced debugging with event breakdown
      const categoryBreakdown = (data || []).reduce((acc, event) => {
        acc[event.event_category] = (acc[event.event_category] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      const typeBreakdown = (data || []).reduce((acc, event) => {
        acc[event.event_type] = (acc[event.event_type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      console.log('[CoreValue Analytics] Recent event categories:', categoryBreakdown);
      console.log('[CoreValue Analytics] Recent event types:', typeBreakdown);
      console.log('[CoreValue Analytics] Sample events:', data?.slice(0, 3));
      
      // Check specifically for content saving and organization events
      const contentSavingEvents = (data || []).filter(e => 
        e.event_category === 'content_saving' || 
        e.event_type.includes('content_saved') ||
        e.event_type.includes('collection_created') ||
        e.event_type.includes('tag_created')
      );
      console.log('[CoreValue Analytics] Content/Organization events found:', contentSavingEvents.length, contentSavingEvents);
    } catch (error) {
      console.error('[CoreValue Analytics] Error in fetchRecentEvents:', error);
    }
  };

  const fetchMetrics = async (showRefreshToast = true) => {
    const wasInitialLoad = isLoading;
    if (!wasInitialLoad) {
      setIsRefreshing(true);
      if (showRefreshToast) {
        toast.info("Refreshing analytics data...");
      }
    }
    
    try {
      // FIXED: Consistent date range calculation for all metrics
      const endDate = new Date();
      endDate.setHours(23, 59, 59, 999); // End of today
      
      const startDate = new Date();
      if (dateRange === 1) {
        // Today: start of today
        startDate.setHours(0, 0, 0, 0);
      } else {
        // For multi-day ranges: exactly N days ago from start of today
        startDate.setDate(startDate.getDate() - (dateRange - 1)); // Include today as day 1
        startDate.setHours(0, 0, 0, 0);
      }

      console.log(`[CoreValue Analytics] Fetching metrics for ${dateRange === 1 ? 'today' : `${dateRange} days`} from ${startDate.toISOString()} to ${endDate.toISOString()}`);

      // First, let's check what events we actually have in the database
      const { data: allEvents, error: allEventsError } = await supabase
        .from('user_activity_events')
        .select('event_type, event_category, timestamp')
        .gte('timestamp', startDate.toISOString())
        .lte('timestamp', endDate.toISOString());

      if (!allEventsError && allEvents) {
        const eventSummary = allEvents.reduce((acc, event) => {
          const key = `${event.event_category}:${event.event_type}`;
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);
        console.log('[CoreValue Analytics] All events in period:', eventSummary);
      }

      // Fetch all metrics in parallel with consistent date ranges
      const [savingResult, orgResult, discoveryResult, engagementResult] = await Promise.all([
        supabase.rpc('get_content_saving_metrics', {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString()
        }),
        supabase.rpc('get_organization_metrics', {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString()
        }),
        supabase.rpc('get_discovery_metrics', {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString()
        }),
        supabase.rpc('get_user_engagement_levels', {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString()
        })
      ]);

      // Check for errors
      if (savingResult.error) {
        console.error('[CoreValue Analytics] Content saving metrics error:', savingResult.error);
        throw savingResult.error;
      }
      if (orgResult.error) {
        console.error('[CoreValue Analytics] Organization metrics error:', orgResult.error);
        throw orgResult.error;
      }
      if (discoveryResult.error) {
        console.error('[CoreValue Analytics] Discovery metrics error:', discoveryResult.error);
        throw discoveryResult.error;
      }
      if (engagementResult.error) {
        console.error('[CoreValue Analytics] Engagement metrics error:', engagementResult.error);
        throw engagementResult.error;
      }

      // Log raw results for debugging
      console.log('[CoreValue Analytics] Raw saving result:', savingResult.data);
      console.log('[CoreValue Analytics] Raw organization result:', orgResult.data);

      // Process the data with consistent expected days
      const expectedDays = dateRange;
      
      const savingData = savingResult.data?.[0];
      if (savingData) {
        console.log('[CoreValue Analytics] Processing saving data:', savingData);
        setContentSaving({
          total_content_saved: savingData.total_content_saved || 0,
          manual_saves: savingData.manual_saves || 0,
          extension_saves: savingData.extension_saves || 0,
          import_saves: savingData.import_saves || 0,
          daily_saves: parseDailyData(savingData.daily_saves, expectedDays)
        });
      } else {
        console.warn('[CoreValue Analytics] No content saving data returned');
        setContentSaving({
          total_content_saved: 0,
          manual_saves: 0,
          extension_saves: 0,
          import_saves: 0,
          daily_saves: []
        });
      }

      const orgData = orgResult.data?.[0];
      if (orgData) {
        console.log('[CoreValue Analytics] Processing organization data:', orgData);
        setOrganization({
          collections_created: orgData.collections_created || 0,
          tags_created: orgData.tags_created || 0,
          boards_created: orgData.boards_created || 0,
          content_organized: orgData.content_organized || 0,
          content_edited: orgData.content_edited || 0,
          daily_organization: parseDailyData(orgData.daily_organization, expectedDays)
        });
      } else {
        console.warn('[CoreValue Analytics] No organization data returned');
        setOrganization({
          collections_created: 0,
          tags_created: 0,
          boards_created: 0,
          content_organized: 0,
          content_edited: 0,
          daily_organization: []
        });
      }

      const discoveryData = discoveryResult.data?.[0];
      if (discoveryData) {
        const processedDailyData = parseDailyData(discoveryData.daily_discovery, expectedDays);
        
        setDiscovery({
          total_navigation: discoveryData.total_navigation || 0,
          collection_navigation: discoveryData.collection_navigation || 0,
          tag_navigation: discoveryData.tag_navigation || 0,
          board_navigation: discoveryData.board_navigation || 0,
          searches_performed: discoveryData.searches_performed || 0,
          search_clicks: discoveryData.search_clicks || 0,
          daily_discovery: processedDailyData
        });
        
        // Enhanced debugging for discovery metrics
        console.log('[CoreValue Analytics] Discovery metrics:', {
          total_navigation: discoveryData.total_navigation,
          collection_navigation: discoveryData.collection_navigation,
          tag_navigation: discoveryData.tag_navigation,
          board_navigation: discoveryData.board_navigation,
          daily_discovery_raw: discoveryData.daily_discovery,
          daily_discovery_processed: processedDailyData,
          expected_days: expectedDays
        });
      } else {
        console.warn('[CoreValue Analytics] No discovery data returned');
        setDiscovery(null);
      }

      const engagementData = engagementResult.data?.[0];
      if (engagementData) {
        setEngagement({
          total_active_users: engagementData.total_active_users || 0,
          saver_count: engagementData.saver_count || 0,
          organizer_count: engagementData.organizer_count || 0,
          discoverer_count: engagementData.discoverer_count || 0,
          curator_count: engagementData.curator_count || 0,
          level_distribution: typeof engagementData.level_distribution === 'object' && engagementData.level_distribution !== null 
            ? engagementData.level_distribution as any 
            : {
                Saver: 0,
                Organizer: 0,
                Discoverer: 0,
                Curator: 0
              }
        });
      } else {
        console.warn('[CoreValue Analytics] No engagement data returned');
        setEngagement(null);
      }

      // Calculate improved data quality
      const qualityScore = calculateDataQuality(savingData, orgData, discoveryData, engagementData);
      setDataQualityScore(qualityScore);

      // Fetch recent events for debugging
      await fetchRecentEvents();

      setLastUpdated(new Date());
      
      if (!wasInitialLoad && showRefreshToast) {
        toast.success("Analytics data refreshed successfully");
      }

      console.log('[CoreValue Analytics] Metrics loaded successfully:', {
        saving: savingData,
        organization: orgData,
        discovery: discoveryData,
        engagement: engagementData,
        dataQuality: qualityScore,
        dateRange: dateRange === 1 ? 'today' : `${dateRange} days`,
        expectedDays: expectedDays,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });

    } catch (error) {
      console.error('[CoreValue Analytics] Error fetching metrics:', error);
      toast.error("Failed to load analytics data");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchMetrics(true);
  };

  useEffect(() => {
    fetchMetrics(false);
  }, [dateRange]);

  // Auto-refresh every 15 seconds (faster for better real-time feel)
  useEffect(() => {
    const interval = setInterval(() => fetchMetrics(false), 15000);
    return () => clearInterval(interval);
  }, [dateRange]);

  // Set up real-time subscription for immediate updates
  useEffect(() => {
    const channel = supabase
      .channel('analytics-updates')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_activity_events'
        },
        () => {
          console.log('[CoreValue Analytics] New event detected, refreshing data');
          fetchMetrics(false);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [dateRange]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
          <span className="ml-3 text-muted-foreground">Loading Core Value metrics...</span>
        </div>
      </div>
    );
  }

  // Calculate search CTR with better handling
  const searchCTR = discovery?.searches_performed > 0 
    ? Math.round(((discovery?.search_clicks || 0) / discovery.searches_performed) * 100)
    : 0;

  // Get display label for date range
  const getDateRangeLabel = () => {
    switch (dateRange) {
      case 1: return 'Today';
      case 7: return 'Last 7 days';
      case 30: return 'Last 30 days';
      case 90: return 'Last 90 days';
      default: return `Last ${dateRange} days`;
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-card-foreground">
                Core Value Features Dashboard
                <Badge variant="outline" className="border-border text-foreground">Phase 1</Badge>
                <Badge 
                  variant={dataQualityScore > 70 ? "default" : dataQualityScore > 40 ? "secondary" : "destructive"}
                  className="text-xs"
                >
                  Data Quality: {dataQualityScore}%
                </Badge>
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Tracking Tier 1 Activities: Content Saving, Organization & Discovery
              </p>
              <div className="flex items-center gap-4 mt-2">
                {lastUpdated && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Last updated: {lastUpdated.toLocaleTimeString()}
                  </p>
                )}
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Activity className="h-3 w-3" />
                  Recent events: {recentEvents.length}
                </p>
                <div className={`w-2 h-2 rounded-full ${isRefreshing ? 'bg-orange-500 animate-pulse' : 'bg-green-500'}`} 
                     title={isRefreshing ? 'Refreshing...' : 'Live data'} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select 
                value={dateRange} 
                onChange={(e) => setDateRange(Number(e.target.value))}
                className="text-sm border border-border bg-background text-foreground rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value={1}>Today</option>
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </select>
              <Button onClick={handleRefresh} variant="outline" size="sm" disabled={isRefreshing}>
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enhanced Data Quality Debug Panel */}
          {dataQualityScore < 50 && (
            <div className="p-3 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <h4 className="font-medium text-sm mb-2 text-yellow-800 dark:text-yellow-200">Data Collection Status</h4>
              <p className="text-xs text-yellow-700 dark:text-yellow-300 mb-2">
                Limited data detected. User actions should appear here within 15-30 seconds.
              </p>
              {recentEvents.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-yellow-800 dark:text-yellow-200">Recent Events by Category:</p>
                  {Object.entries(
                    recentEvents.reduce((acc, event) => {
                      acc[event.event_category] = (acc[event.event_category] || 0) + 1;
                      return acc;
                    }, {} as Record<string, number>)
                  ).map(([category, count]) => (
                    <p key={category} className="text-xs text-yellow-700 dark:text-yellow-300">
                      {category}: {count} events
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Content Saving Metrics */}
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2 text-card-foreground">
              <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              Content Saving Activity ({getDateRangeLabel()})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <CoreValueMetricCard
                title="Total Content Saved"
                value={contentSaving?.total_content_saved || 0}
                icon={<FolderPlus className="h-4 w-4" />}
                color="blue"
              />
              <CoreValueMetricCard
                title="Manual Saves"
                value={contentSaving?.manual_saves || 0}
                subtitle={`${Math.round(((contentSaving?.manual_saves || 0) / (contentSaving?.total_content_saved || 1)) * 100)}%`}
                color="green"
              />
              <CoreValueMetricCard
                title="Extension Saves"
                value={contentSaving?.extension_saves || 0}
                subtitle={`${Math.round(((contentSaving?.extension_saves || 0) / (contentSaving?.total_content_saved || 1)) * 100)}%`}
                color="purple"
              />
              <CoreValueMetricCard
                title="Import Saves"
                value={contentSaving?.import_saves || 0}
                subtitle={`${Math.round(((contentSaving?.import_saves || 0) / (contentSaving?.total_content_saved || 1)) * 100)}%`}
                color="orange"
              />
            </div>
            {contentSaving?.daily_saves && contentSaving.daily_saves.length > 0 ? (
              <CoreValueChart
                data={contentSaving.daily_saves}
                title={`Daily Content Saving Trend (${getDateRangeLabel()})`}
                color="#3B82F6"
              />
            ) : (
              <div className="p-4 bg-muted/50 border border-border rounded-lg text-center">
                <p className="text-sm text-muted-foreground">No content saving data available for {getDateRangeLabel().toLowerCase()}</p>
              </div>
            )}
          </div>

          {/* Organization Metrics */}
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2 text-card-foreground">
              <FolderPlus className="h-5 w-5 text-green-600 dark:text-green-400" />
              Organization & Curation Activity ({getDateRangeLabel()})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
              <CoreValueMetricCard
                title="Collections Created"
                value={organization?.collections_created || 0}
                color="blue"
              />
              <CoreValueMetricCard
                title="Tags Created"
                value={organization?.tags_created || 0}
                color="green"
              />
              <CoreValueMetricCard
                title="Boards Created"
                value={organization?.boards_created || 0}
                color="purple"
              />
              <CoreValueMetricCard
                title="Content Organized"
                value={organization?.content_organized || 0}
                color="orange"
              />
              <CoreValueMetricCard
                title="Content Edited"
                value={organization?.content_edited || 0}
                color="red"
              />
            </div>
            {organization?.daily_organization && organization.daily_organization.length > 0 ? (
              <CoreValueChart
                data={organization.daily_organization}
                title={`Daily Organization Activity (${getDateRangeLabel()})`}
                color="#10B981"
              />
            ) : (
              <div className="p-4 bg-muted/50 border border-border rounded-lg text-center">
                <p className="text-sm text-muted-foreground">No organization data available for {getDateRangeLabel().toLowerCase()}</p>
              </div>
            )}
          </div>

          {/* Discovery Metrics */}
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2 text-card-foreground">
              <Search className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              Content Discovery Activity ({getDateRangeLabel()})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <CoreValueMetricCard
                title="Total Navigation Events"
                value={discovery?.total_navigation || 0}
                icon={<Search className="h-4 w-4" />}
                color="purple"
                subtitle="Collections, tags, boards"
              />
              <CoreValueMetricCard
                title="Searches Performed"
                value={discovery?.searches_performed || 0}
                subtitle="Search queries"
                color="blue"
              />
              <CoreValueMetricCard
                title="Search Result Clicks"
                value={discovery?.search_clicks || 0}
                subtitle={`${searchCTR}% CTR`}
                color="green"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <CoreValueMetricCard
                title="Collection Navigation"
                value={discovery?.collection_navigation || 0}
                color="blue"
              />
              <CoreValueMetricCard
                title="Tag Navigation"
                value={discovery?.tag_navigation || 0}
                color="green"
              />
              <CoreValueMetricCard
                title="Board Navigation"
                value={discovery?.board_navigation || 0}
                color="purple"
              />
            </div>
            {discovery?.daily_discovery && (
              <CoreValueChart
                data={discovery.daily_discovery}
                title={`Daily Discovery Activity (${getDateRangeLabel()})`}
                color="#8B5CF6"
              />
            )}
          </div>

          {/* User Engagement Progression */}
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2 text-card-foreground">
              <Users className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              User Level Distribution (7-day activity)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
              <CoreValueMetricCard
                title="Total Active Users"
                value={engagement?.total_active_users || 0}
                color="blue"
              />
              <CoreValueMetricCard
                title="Savers"
                value={engagement?.saver_count || 0}
                subtitle="Level 1"
                color="green"
              />
              <CoreValueMetricCard
                title="Organizers"
                value={engagement?.organizer_count || 0}
                subtitle="Level 2"
                color="blue"
              />
              <CoreValueMetricCard
                title="Discoverers"
                value={engagement?.discoverer_count || 0}
                subtitle="Level 3"
                color="purple"
              />
              <CoreValueMetricCard
                title="Curators"
                value={engagement?.curator_count || 0}
                subtitle="Level 4"
                color="red"
              />
            </div>
            
            {/* Level Distribution Visualization */}
            {engagement?.level_distribution && (
              <div className="mt-4 p-4 bg-muted/50 border border-border rounded-lg">
                <h4 className="font-medium mb-3 text-card-foreground">User Level Breakdown</h4>
                <div className="space-y-3">
                  {Object.entries(engagement.level_distribution).map(([level, count]) => {
                    const percentage = engagement.total_active_users > 0 
                      ? Math.round((count / engagement.total_active_users) * 100)
                      : 0;
                    
                    const colorMap = {
                      'Saver': 'bg-green-500',
                      'Organizer': 'bg-blue-500', 
                      'Discoverer': 'bg-purple-500',
                      'Curator': 'bg-gradient-to-r from-blue-500 to-pink-500'
                    };
                    
                    return (
                      <div key={level} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground min-w-20">{level}:</span>
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-muted rounded-full h-2">
                              <div 
                                className={`${colorMap[level as keyof typeof colorMap]} h-2 rounded-full`}
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-muted-foreground">{percentage}%</span>
                          </div>
                        </div>
                        <span className="text-sm font-medium text-foreground">{count}</span>
                      </div>
                    );
                  })}
                </div>
                
                {/* Summary */}
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    {engagement.total_active_users} total users: {' '}
                    {Object.entries(engagement.level_distribution)
                      .filter(([_, count]) => count > 0)
                      .map(([level, count]) => `${count} ${level}${count > 1 ? 's' : ''}`)
                      .join(', ')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Engagement Definitions */}
      <EngagementDefinitions />
      
      {/* MOVED: Analytics Debug Panel to bottom */}
      <AnalyticsDebugPanel />
    </div>
  );
};

export default CoreValueDashboard;
